import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Btn, Eyebrow } from "../components/Primitives";

const BACKDROP_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const PANEL_RISE = { initial: { y: 20, opacity: 0 }, animate: { y: 0, opacity: 1 } };
const SCALE_FROM_ZERO = { initial: { scale: 0 }, animate: { scale: 1 } };

export default function RatingPrompt({ onContinue }) {
    const [stage, setStage] = useState("ask"); // ask | modal | rated

    const openModal = () => {
        setStage("modal");
        setTimeout(() => setStage("rated"), 2000);
    };

    return (
        <div className="flex-1 flex flex-col bg-cream relative" data-testid="rating-screen">
            <div className="flex-1 flex flex-col justify-center px-7">
                <Eyebrow className="mb-5">A small ask</Eyebrow>
                <h2 className="font-display text-4xl leading-[1.15] text-burgundy tracking-tight">
                    We're a small team building something we believe in.
                </h2>
                <p className="mt-4 font-display italic text-2xl text-ink-muted leading-snug">
                    A rating means everything.
                </p>

                {/* Decorative stars (gold outline, no emoji) */}
                <div className="mt-10 flex gap-2 text-gold">
                    {[...Array(5)].map((_, i) => (
                        <svg
                            key={`star-${i}`}
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="opacity-90"
                        >
                            <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.78 1.401 8.166L12 18.896l-7.335 3.861 1.401-8.166L.132 9.211l8.2-1.193z" />
                        </svg>
                    ))}
                </div>
            </div>

            <div className="px-6 pb-10 space-y-3">
                {stage === "ask" && (
                    <Btn data-testid="rating-leave-btn" onClick={openModal}>
                        Leave a rating
                    </Btn>
                )}
                {stage === "rated" && (
                    <>
                        <Btn data-testid="rating-continue-btn" onClick={onContinue}>
                            I rated, continue
                        </Btn>
                        <p className="text-center text-[10px] tracking-[0.28em] uppercase text-ink-muted">
                            Thank you. Truly.
                        </p>
                    </>
                )}
            </div>

            <AnimatePresence>
                {stage === "modal" && (
                    <motion.div
                        initial={BACKDROP_FADE.initial}
                        animate={BACKDROP_FADE.animate}
                        exit={BACKDROP_FADE.exit}
                        className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 px-6"
                        data-testid="rating-modal"
                    >
                        <motion.div
                            initial={PANEL_RISE.initial}
                            animate={PANEL_RISE.animate}
                            className="w-full max-w-xs bg-cream border border-burgundy/20 p-6 text-center"
                        >
                            <Eyebrow>Rate this app</Eyebrow>
                            <div className="mt-4 flex justify-center gap-1.5 text-gold">
                                {[...Array(5)].map((_, i) => (
                                    <motion.svg
                                        key={`modal-star-${i}`}
                                        initial={SCALE_FROM_ZERO.initial}
                                        animate={SCALE_FROM_ZERO.animate}
                                        transition={{ delay: i * 0.12, duration: 0.3 }}
                                        width="26"
                                        height="26"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.78 1.401 8.166L12 18.896l-7.335 3.861 1.401-8.166L.132 9.211l8.2-1.193z" />
                                    </motion.svg>
                                ))}
                            </div>
                            <p className="mt-4 text-sm text-ink-muted font-light">
                                Submitting your rating…
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
